import { useCallback, useEffect, useState } from 'react';
import {
  getSystemDefaults,
  patchSystemInChannelReminders,
  patchSystemReminderCadence,
} from '../api/client';
import type { InChannelRemindersMode, SystemSettingsDefaultsResponse } from '../api/types';
import { CadenceDefaultRow, InChannelDefaultRow } from '../settings/TrackingDefaultRows';

export function SystemSettingsPage() {
  const [data, setData] = useState<SystemSettingsDefaultsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const defaults = await getSystemDefaults();
      setData(defaults);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load system settings');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSaveCadence(minutes: number) {
    const next = await patchSystemReminderCadence(minutes);
    setData((prev) => (prev ? { ...prev, cadence: next } : prev));
  }

  async function handleResetCadence() {
    const next = await patchSystemReminderCadence(null);
    setData((prev) => (prev ? { ...prev, cadence: next } : prev));
  }

  async function handleSaveInChannel(mode: InChannelRemindersMode) {
    const next = await patchSystemInChannelReminders(mode);
    setData((prev) => (prev ? { ...prev, inChannelReminders: next } : prev));
  }

  async function handleResetInChannel() {
    const next = await patchSystemInChannelReminders(null);
    setData((prev) => (prev ? { ...prev, inChannelReminders: next } : prev));
  }

  return (
    <div style={styles.layout} data-testid="system-settings-page">
      <h1 style={styles.title}>System settings</h1>
      <p style={styles.intro}>
        Defaults for all workspaces that do not have a workspace override. Applies to new tracks.
      </p>
      {loading && <p>Loading…</p>}
      {error && (
        <div data-testid="system-settings-error">
          <p style={styles.error}>{error}</p>
          <button
            type="button"
            onClick={() => void load()}
            style={styles.retry}
            data-testid="system-settings-retry"
          >
            Retry
          </button>
        </div>
      )}
      {data && (
        <div style={styles.section}>
          <CadenceDefaultRow
            setting={data.cadence}
            canReset={data.cadence.layer === 'system'}
            onSave={handleSaveCadence}
            onReset={handleResetCadence}
          />
          <InChannelDefaultRow
            setting={data.inChannelReminders}
            canReset={data.inChannelReminders.layer === 'system'}
            onSave={handleSaveInChannel}
            onReset={handleResetInChannel}
          />
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  layout: {
    maxWidth: 900,
    margin: '0 auto',
    padding: 24,
  },
  title: {
    margin: '0 0 8px',
    fontSize: 24,
    fontWeight: 600,
    color: '#1a1a1a',
  },
  intro: {
    margin: '0 0 24px',
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 1.5,
  },
  section: {
    background: '#fff',
    padding: 24,
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  error: {
    color: '#b91c1c',
    marginBottom: 12,
  },
  retry: {
    padding: '8px 14px',
    fontSize: 14,
    border: '1px solid #d1d5db',
    borderRadius: 6,
    background: '#fff',
    cursor: 'pointer',
  },
};
